// Thunks for the Slot Availability Calendar.
//
// Phase 1 wires only the FIVE read thunks the customer/owner card (P2/P3)
// consumes immediately: calendar meta (needed to tell owner from
// not-configured), and public+owner availability/summary. Mutation thunks
// (publish, schedule versions, slots, booking entries, blocks) are
// deliberately deferred to the phase that first ships their UI — see the
// plan doc's phase list. Wiring a thunk with no caller yet can't be verified
// by anything except tsc, and each of those mutations also needs its own
// conflict-handling wiring via SlotCalendarMutationHelper, which is easier to
// get right alongside the screen that will immediately exercise it.
//
// Convention: every thunk rejects with a friendly STRING (already produced by
// SlotCalendarApiService's toFriendlySlotCalendarError), same as every other
// thunk file in this app, so a reducer can assign action.payload straight
// into errorMap. The one exception is calendar-not-found, which rejects with
// the sentinel below so the slice can render "not configured" as a STATE
// rather than an error — the same distinction ADMIN_ACCESS_DENIED draws in
// AdminBusinessesThunk.ts.

import { createAsyncThunk } from "@reduxjs/toolkit";
import SlotCalendarApiService, {
  GetBookingEntriesInput,
  isCalendarNotFoundError,
  isStaleRevisionError,
  toFriendlySlotCalendarError,
  hasSlotCalendarErrorCode,
  SLOT_CALENDAR_ERROR_CODES,
} from "../apiServices/SlotCalendarApiService";
import { mintIdempotencyKey } from "../helpers/SlotCalendarMutationHelper";
import {
  CreateAvailabilitySlotInput,
  CreateSlotBlockInput,
  CreateSlotBookingEntryInput,
  CreateSlotScheduleVersionInput,
  PreviewSlotCalendarInput,
  PreviewSlotScheduleInput,
  SlotAvailabilityPage,
  SlotBlockPreview,
  SlotBookingEntry,
  SlotCalendarMeta,
  SlotCalendarMutationResult,
  SlotCalendarPage,
  SlotCalendarPreview,
  SlotCalendarSummary,
  SlotOwnerAvailabilityPage,
  SlotOwnerAvailabilitySlot,
  SlotSummaryBucket,
  UpdateAvailabilitySlotInput,
  UpdateSlotBookingEntryInput,
} from "../../interfaces/SlotCalendarObject";
import {
  buildAvailabilityRangeKey,
  buildSummaryRangeKey,
} from "../helpers/SlotCalendarRangeKeyHelper";

export const SLOT_CALENDAR_NOT_FOUND = "SLOT_CALENDAR_NOT_FOUND";
/** 14303 - signed in, but not the product's effective (business) owner. */
export const SLOT_CALENDAR_NOT_OWNER = "SLOT_CALENDAR_NOT_OWNER";
/** 14306/14307 - listing can't have a calendar (no business / not a rental). */
export const SLOT_CALENDAR_UNSUPPORTED = "SLOT_CALENDAR_UNSUPPORTED";
/** 14328 on record-booking - this entryId was already saved (a retry of the
 *  same action), so the caller treats it as success. */
export const SLOT_CALENDAR_ENTRY_EXISTS = "SLOT_CALENDAR_ENTRY_EXISTS";

// ─── Calendar meta ───────────────────────────────────────────────────────────

/** Owner GET B. Also the way the widget first learns whether the current
 *  viewer even has an owner-shaped calendar to manage — a customer never
 *  calls this (see useSlotCalendarViewer's viewer gate, P2). */
export const fetchSlotCalendarMeta = createAsyncThunk<
  { productId: string; meta: SlotCalendarMeta; etag: string | null },
  string,
  { rejectValue: string }
>("slotCalendar/fetchMeta", async (productId, { rejectWithValue }) => {
  try {
    const { meta, etag } = await SlotCalendarApiService.getSlotCalendar(productId);
    return { productId, meta, etag };
  } catch (error) {
    if (isCalendarNotFoundError(error)) return rejectWithValue(SLOT_CALENDAR_NOT_FOUND);
    if (hasSlotCalendarErrorCode(error, SLOT_CALENDAR_ERROR_CODES.NOT_EFFECTIVE_OWNER)) {
      return rejectWithValue(SLOT_CALENDAR_NOT_OWNER);
    }
    if (
      hasSlotCalendarErrorCode(error, SLOT_CALENDAR_ERROR_CODES.RENTAL_LISTING_REQUIRED) ||
      hasSlotCalendarErrorCode(error, SLOT_CALENDAR_ERROR_CODES.BUSINESS_REQUIRED)
    ) {
      return rejectWithValue(SLOT_CALENDAR_UNSUPPORTED);
    }
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

// ─── Public availability (customer card) ────────────────────────────────────

export interface FetchPublicAvailabilityArg {
  productId: string;
  fromIso: string;
  toIsoExclusive: string;
  cursor?: string;
  limit?: number;
}

export const fetchPublicSlotAvailability = createAsyncThunk<
  { productId: string; rangeKey: string; page: SlotAvailabilityPage },
  FetchPublicAvailabilityArg,
  { rejectValue: string }
>("slotCalendar/fetchPublicAvailability", async (arg, { rejectWithValue }) => {
  try {
    const page = await SlotCalendarApiService.getAllPublicAvailability(arg.productId, arg);
    return { productId: arg.productId, rangeKey: buildAvailabilityRangeKey(arg), page };
  } catch (error) {
    // A public 404 here can't be told apart from "unpublished" (plan doc §7
    // Q4) - reuse the same sentinel fetchSlotCalendarMeta rejects with so
    // useSlotCalendarView (P2) can render its "not published yet" state
    // instead of a generic, misleadingly-retryable error.
    if (isCalendarNotFoundError(error)) return rejectWithValue(SLOT_CALENDAR_NOT_FOUND);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface FetchAvailabilitySummaryArg {
  productId: string;
  fromDate: string;
  toDateExclusive: string;
  bucket: SlotSummaryBucket;
}

export const fetchPublicSlotAvailabilitySummary = createAsyncThunk<
  { productId: string; rangeKey: string; summary: SlotCalendarSummary },
  FetchAvailabilitySummaryArg,
  { rejectValue: string }
>("slotCalendar/fetchPublicAvailabilitySummary", async (arg, { rejectWithValue }) => {
  try {
    const summary = await SlotCalendarApiService.getPublicAvailabilitySummary(arg.productId, arg);
    return { productId: arg.productId, rangeKey: buildSummaryRangeKey(arg), summary };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

// ─── Owner availability (Manage screen, and the card's owner states) ────────

export interface FetchOwnerAvailabilityArg extends FetchPublicAvailabilityArg {
  includeArchived?: boolean;
}

export const fetchOwnerSlotAvailability = createAsyncThunk<
  { productId: string; rangeKey: string; page: SlotOwnerAvailabilityPage },
  FetchOwnerAvailabilityArg,
  { rejectValue: string }
>("slotCalendar/fetchOwnerAvailability", async (arg, { rejectWithValue }) => {
  try {
    const page = await SlotCalendarApiService.getAllOwnerAvailability(arg.productId, arg);
    return { productId: arg.productId, rangeKey: buildAvailabilityRangeKey(arg), page };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface FetchOwnerSummaryArg extends FetchAvailabilitySummaryArg {
  includeArchived?: boolean;
}

export const fetchOwnerSlotSummary = createAsyncThunk<
  { productId: string; rangeKey: string; summary: SlotCalendarSummary },
  FetchOwnerSummaryArg,
  { rejectValue: string }
>("slotCalendar/fetchOwnerSummary", async (arg, { rejectWithValue }) => {
  try {
    const summary = await SlotCalendarApiService.getOwnerSummary(arg.productId, arg);
    return { productId: arg.productId, rangeKey: buildSummaryRangeKey(arg), summary };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

// ─── Publish (first mutation - P4) ──────────────────────────────────────────

export const SLOT_CALENDAR_STALE_REVISION = "SLOT_CALENDAR_STALE_REVISION";

export interface PublishSlotCalendarArg {
  productId: string;
}

/** PUT B only ever changes `published` on an existing calendar. Per the
 *  backend handover it always reads the calendar first and PUTs with THAT
 *  ETag and THAT timezone (timezone is immutable) - so a cached ETag that
 *  went stale in another tab can't turn a publish toggle into a 412. */
async function putCalendarPublishedState(
  productId: string,
  published: boolean
): Promise<{ productId: string; etag: string | null }> {
  const current = await SlotCalendarApiService.getSlotCalendar(productId);
  if (!current.etag) throw new Error("Couldn't confirm the calendar's current version. Please try again.");
  const { etag: newEtag } = await SlotCalendarApiService.putSlotCalendar(
    productId,
    { timezone: current.meta.timezone, published },
    { ifMatch: current.etag, idempotencyKey: mintIdempotencyKey() }
  );
  return { productId, etag: newEtag };
}

/** PUT B with published:true. The mutation result doesn't echo back a full
 *  SlotCalendarMeta (just calendarId/revision/etc - see
 *  SlotCalendarMutationResult), so the caller re-fetches meta after a
 *  success rather than this thunk trying to reconstruct it. */
export const publishSlotCalendar = createAsyncThunk<
  { productId: string; etag: string | null },
  PublishSlotCalendarArg,
  { rejectValue: string }
>("slotCalendar/publish", async ({ productId }, { rejectWithValue }) => {
  try {
    return await putCalendarPublishedState(productId, true);
  } catch (error) {
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

/** PUT B with published:false (P6's Manage top bar). Same conflict handling
 *  as publish - a stale etag means someone/something changed the calendar
 *  since this screen loaded it, so the caller refetches meta rather than
 *  guessing. */
export const unpublishSlotCalendar = createAsyncThunk<
  { productId: string; etag: string | null },
  PublishSlotCalendarArg,
  { rejectValue: string }
>("slotCalendar/unpublish", async ({ productId }, { rejectWithValue }) => {
  try {
    return await putCalendarPublishedState(productId, false);
  } catch (error) {
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

// ─── Setup (P5) ─────────────────────────────────────────────────────────

export interface PreviewSlotCalendarSetupArg {
  /** Keying only — POST /slot-calendar-previews takes no productId (it's
   *  the productless dry run the plan doc's Step 3 "count box" uses, since
   *  no calendar exists yet at that point in the wizard). */
  productId: string;
  input: PreviewSlotCalendarInput;
}

export const previewSlotCalendarSetup = createAsyncThunk<
  { productId: string; preview: SlotCalendarPreview },
  PreviewSlotCalendarSetupArg,
  { rejectValue: string }
>("slotCalendar/previewSetup", async ({ productId, input }, { rejectWithValue }) => {
  try {
    const preview = await SlotCalendarApiService.previewCalendar(input);
    return { productId, preview };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface CreateSlotCalendarArg {
  productId: string;
  timezone: string;
}

/** PUT B, If-None-Match: * — creates the calendar as an unpublished draft.
 *  The first of Setup's two writes; useSlotCalendarSetup skips this one
 *  entirely on a resumed draft that already has a calendarId, reusing its
 *  existing etag/timezone instead (plan doc's "resumable" requirement). */
export const createSlotCalendar = createAsyncThunk<
  {
    productId: string;
    etag: string | null;
    /** Non-null when the calendar ALREADY existed (an earlier attempt created
     *  it) - nothing was written; the caller resumes this one, using its
     *  timezone and checking whether it already has a schedule. */
    existing: SlotCalendarMeta | null;
  },
  CreateSlotCalendarArg,
  { rejectValue: string }
>("slotCalendar/create", async ({ productId, timezone }, { rejectWithValue }) => {
  try {
    // Ask the server first (backend 412 handover): 200 -> it exists, resume;
    // 404 -> create with If-None-Match: *.
    const current = await SlotCalendarApiService.findSlotCalendar(productId);
    if (current) return { productId, etag: current.etag, existing: current.meta };

    const { etag } = await SlotCalendarApiService.putSlotCalendar(
      productId,
      { timezone, published: false },
      { idempotencyKey: mintIdempotencyKey() }
    );
    return { productId, etag, existing: null };
  } catch (error) {
    // Created by someone else between our GET and PUT - caller reloads.
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface CreateSlotScheduleVersionArg {
  productId: string;
  input: CreateSlotScheduleVersionInput;
  etag: string;
}

/** POST schedule-versions — Setup's second write, chained off the etag the
 *  calendar create (or a resumed meta fetch) produced. Same 412 handling as
 *  publish: the caller refetches meta and lets the person retry rather than
 *  silently losing their wizard inputs. */
export const createSlotScheduleVersion = createAsyncThunk<
  { productId: string; result: SlotCalendarMutationResult; etag: string | null },
  CreateSlotScheduleVersionArg,
  { rejectValue: string }
>("slotCalendar/createScheduleVersion", async ({ productId, input, etag }, { rejectWithValue }) => {
  try {
    const { result, etag: newEtag } = await SlotCalendarApiService.createScheduleVersion(productId, input, {
      idempotencyKey: mintIdempotencyKey(),
      ifMatch: etag,
    });
    return { productId, result, etag: newEtag };
  } catch (error) {
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

// ─── Manage screen reads (P6) ───────────────────────────────────────────

export interface FetchOwnerSlotDetailArg {
  productId: string;
  slotId: string;
  occurrenceStart?: string;
}

/** GET B/slots/{slotId} - the read-only Slot sheet's status/capacity/notes. */
export const fetchOwnerSlotDetail = createAsyncThunk<
  { productId: string; slot: SlotOwnerAvailabilitySlot },
  FetchOwnerSlotDetailArg,
  { rejectValue: string }
>("slotCalendar/fetchOwnerSlotDetail", async ({ productId, slotId, occurrenceStart }, { rejectWithValue }) => {
  try {
    const slot = await SlotCalendarApiService.getOwnerSlotDetail(productId, slotId, occurrenceStart);
    return { productId, slot };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface FetchBookingEntriesArg extends GetBookingEntriesInput {
  productId: string;
  slotId: string;
}

/** GET B/slots/{slotId}/booking-entries - the Slot sheet's bookings list
 *  (P6 reads only the first page; Cancel/Undo and further pages are P7). */
export const fetchBookingEntries = createAsyncThunk<
  { productId: string; slotId: string; page: SlotCalendarPage<SlotBookingEntry> },
  FetchBookingEntriesArg,
  { rejectValue: string }
>("slotCalendar/fetchBookingEntries", async ({ productId, slotId, ...input }, { rejectWithValue }) => {
  try {
    const page = await SlotCalendarApiService.getBookingEntries(productId, slotId, input);
    return { productId, slotId, page };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

// ─── Slot sheet writes (P7) ─────────────────────────────────────────
// Same conflict handling as every other write here: a 412 rejects with the
// stale-revision sentinel so the caller can refetch meta and let the person
// retry rather than lose their form inputs.

export interface RecordBookingEntryArg {
  productId: string;
  slotId: string;
  input: CreateSlotBookingEntryInput;
  etag: string;
}

/** POST .../slots/{slotId}/booking-entries - the sheet's "Record a booking"
 *  form. `input.entryId` is minted once by the caller when the form opens
 *  and reused across any resubmit of that same intent (plan doc's "double-
 *  tap Save (one entry only)" test), not minted fresh here. */
export const recordBookingEntry = createAsyncThunk<
  { productId: string; slotId: string; result: SlotCalendarMutationResult; etag: string | null },
  RecordBookingEntryArg,
  { rejectValue: string }
>("slotCalendar/recordBookingEntry", async ({ productId, slotId, input, etag }, { rejectWithValue }) => {
  try {
    const { result, etag: newEtag } = await SlotCalendarApiService.createBookingEntry(productId, slotId, input, {
      idempotencyKey: mintIdempotencyKey(),
      ifMatch: etag,
    });
    return { productId, slotId, result, etag: newEtag };
  } catch (error) {
    if (hasSlotCalendarErrorCode(error, SLOT_CALENDAR_ERROR_CODES.BOOKING_ENTRY_ALREADY_EXISTS)) {
      return rejectWithValue(SLOT_CALENDAR_ENTRY_EXISTS);
    }
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface UpdateBookingEntryArg {
  productId: string;
  slotId: string;
  entryId: string;
  input: UpdateSlotBookingEntryInput;
  etag: string;
}

/** PUT .../booking-entries/{entryId} - Cancel (status: CANCELLED) and Undo
 *  (status: ACTIVE) are the same write with the opposite status. */
export const updateBookingEntry = createAsyncThunk<
  { productId: string; slotId: string; result: SlotCalendarMutationResult; etag: string | null },
  UpdateBookingEntryArg,
  { rejectValue: string }
>("slotCalendar/updateBookingEntry", async ({ productId, slotId, entryId, input, etag }, { rejectWithValue }) => {
  try {
    const { result, etag: newEtag } = await SlotCalendarApiService.updateBookingEntry(productId, slotId, entryId, input, {
      idempotencyKey: mintIdempotencyKey(),
      ifMatch: etag,
    });
    return { productId, slotId, result, etag: newEtag };
  } catch (error) {
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface UpdateAvailabilitySlotArg {
  productId: string;
  slotId: string;
  input: UpdateAvailabilitySlotInput;
  etag: string;
}

/** PUT .../slots/{slotId} - the sheet's Maintenance/Unavailable/Reopen
 *  status buttons. Callers send every field of UpdateAvailabilitySlotInput
 *  (it's a full replace, not a patch), copying the rest forward from the
 *  slot already in hand and changing only statusOverride/reapplyStatusOverride. */
export const updateAvailabilitySlot = createAsyncThunk<
  { productId: string; slotId: string; result: SlotCalendarMutationResult; etag: string | null },
  UpdateAvailabilitySlotArg,
  { rejectValue: string }
>("slotCalendar/updateAvailabilitySlot", async ({ productId, slotId, input, etag }, { rejectWithValue }) => {
  try {
    const { result, etag: newEtag } = await SlotCalendarApiService.updateAvailabilitySlot(productId, slotId, input, {
      idempotencyKey: mintIdempotencyKey(),
      ifMatch: etag,
    });
    return { productId, slotId, result, etag: newEtag };
  } catch (error) {
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface SlotBlockArg {
  productId: string;
  input: CreateSlotBlockInput;
}

/** POST .../block-previews - the Block dates sheet's dry-run impact box (P8).
 *  Read-only on the server, so no etag/idempotency key. */
export const previewSlotBlock = createAsyncThunk<
  { productId: string; preview: SlotBlockPreview },
  SlotBlockArg,
  { rejectValue: string }
>("slotCalendar/previewBlock", async ({ productId, input }, { rejectWithValue }) => {
  try {
    const preview = await SlotCalendarApiService.previewBlock(productId, input);
    return { productId, preview };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface CreateSlotBlockArg extends SlotBlockArg {
  etag: string;
}

/** POST .../blocks - applies a Maintenance/Unavailable range. Callers pass
 *  allowBookedSlots: true only after the owner acknowledged the preview's
 *  "N bookings affected" warning (BLOCK_AFFECTS_BOOKINGS otherwise). */
export const createSlotBlock = createAsyncThunk<
  { productId: string; result: SlotCalendarMutationResult; etag: string | null },
  CreateSlotBlockArg,
  { rejectValue: string }
>("slotCalendar/createBlock", async ({ productId, input, etag }, { rejectWithValue }) => {
  try {
    const { result, etag: newEtag } = await SlotCalendarApiService.createBlock(productId, input, {
      idempotencyKey: mintIdempotencyKey(),
      ifMatch: etag,
    });
    return { productId, result, etag: newEtag };
  } catch (error) {
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface PreviewSlotScheduleArg {
  productId: string;
  input: PreviewSlotScheduleInput;
}

/** POST .../schedule-previews - Add slots' (P9) dry run of the complete next
 *  version: new occurrence count + conflicts before anything is written. */
export const previewSlotSchedule = createAsyncThunk<
  { productId: string; preview: SlotCalendarPreview },
  PreviewSlotScheduleArg,
  { rejectValue: string }
>("slotCalendar/previewSchedule", async ({ productId, input }, { rejectWithValue }) => {
  try {
    const preview = await SlotCalendarApiService.previewSchedule(productId, input);
    return { productId, preview };
  } catch (error) {
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

export interface CreateOneOffSlotArg {
  productId: string;
  input: CreateAvailabilitySlotInput;
  etag: string;
}

/** POST .../slots - one irregular slot outside the recurring schedule (P9). */
export const createOneOffSlot = createAsyncThunk<
  { productId: string; result: SlotCalendarMutationResult; etag: string | null },
  CreateOneOffSlotArg,
  { rejectValue: string }
>("slotCalendar/createOneOffSlot", async ({ productId, input, etag }, { rejectWithValue }) => {
  try {
    const { result, etag: newEtag } = await SlotCalendarApiService.createAvailabilitySlot(productId, input, {
      idempotencyKey: mintIdempotencyKey(),
      ifMatch: etag,
    });
    return { productId, result, etag: newEtag };
  } catch (error) {
    if (isStaleRevisionError(error)) return rejectWithValue(SLOT_CALENDAR_STALE_REVISION);
    return rejectWithValue(toFriendlySlotCalendarError(error));
  }
});

const SlotCalendarThunks = {
  fetchSlotCalendarMeta,
  fetchPublicSlotAvailability,
  fetchPublicSlotAvailabilitySummary,
  fetchOwnerSlotAvailability,
  fetchOwnerSlotSummary,
  publishSlotCalendar,
  previewSlotCalendarSetup,
  createSlotCalendar,
  createSlotScheduleVersion,
  unpublishSlotCalendar,
  fetchOwnerSlotDetail,
  fetchBookingEntries,
  recordBookingEntry,
  updateBookingEntry,
  updateAvailabilitySlot,
  previewSlotBlock,
  createSlotBlock,
  previewSlotSchedule,
  createOneOffSlot,
};

export default SlotCalendarThunks;
